// ⚠️ #1522 — the console.warn calls in this file stay warn, deliberately.
//
// main.tsx runs captureConsoleIntegration at levels ["error"], so a warn
// produces no Sentry event. That is the point here: the polling tick
// fails on a timer or a shim, nothing she did depends on it, and nothing is
// waiting on the result. The repo's rule is "warn means expected and handled,
// error means a human should see it" — these are the warns that rule exists
// for, and converting them is the quota flood main.tsx's comment warns about.
//
// A failed WRITE she initiated does not belong here. If one is added to this
// file, report it with reportError.

import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import { openMyKinTo } from "@/lib/myKinEvents";
import {
  PRESENCE_HEARTBEAT_MS,
  bumpPresence,
  claimInAppNotification,
  fetchPendingInAppNotifications,
  runInAppNotificationTick,
  type PendingInAppNotification,
} from "@/lib/inAppNotifications";

/**
 * Issue #803 — she gets a toast, not a text, when she's still in the app.
 *
 * Mounted once, next to EntitlementFlipListener. Renders nothing. Every tick it
 * tells the server she's here and picks up anything the server left for the app
 * to say instead of pushing. The full handoff (and why the claim comes before
 * the toast) is documented in src/lib/inAppNotifications.ts.
 *
 * ── Why it ticks rather than subscribes ────────────────────────────────────
 *
 * There is no Supabase Realtime anywhere in this app, and the heartbeat has to
 * happen on a timer regardless — the server cannot know she's present unless
 * something says so periodically. Given a timer already running, folding the
 * poll into the same tick costs one extra request a minute and removes a whole
 * class of bug: the client cannot be polling on a schedule the server thinks is
 * stale, because they are the same tick. A realtime subscription would still
 * have needed the heartbeat next to it.
 */

/** Toast copy comes from the row; the action target is a small closed set. */
const runAction = (target: string | null): (() => void) | null => {
  if (target === "grocery") return () => openMyKinTo({ section: "grocery" });
  return null;
};

const showNotificationToast = (notification: PendingInAppNotification): void => {
  const action = runAction(notification.action_target);
  toast.success(notification.toast_title, {
    description: notification.toast_body,
    duration: 8000,
    ...(action ? { action: { label: "See it", onClick: action } } : {}),
  });
};

const InAppNotificationListener = () => {
  const { user } = useAuth();
  // One set for the component's whole life, not per tick — its job is to stop
  // two OVERLAPPING ticks from claiming the same row, so it has to outlive one.
  const inFlight = useRef<Set<string>>(new Set());

  useEffect(() => {
    const userId = user?.id;
    if (!userId) return;

    let cancelled = false;
    let running = false;

    const tick = async (trigger: string) => {
      if (cancelled) return;
      // A visible tab is the entire claim being made to the server. Bumping
      // presence from a backgrounded tab would tell it she's looking at
      // something she can't see, and the toast would go to an empty room while
      // the push she should have had was suppressed.
      if (document.visibilityState !== "visible") return;
      // A slow tick must not stack with the next one — the in-flight set covers
      // duplicate claims, but there is no reason to pile up requests either.
      if (running) return;
      running = true;
      try {
        const shown = await runInAppNotificationTick(userId, {
          bump: bumpPresence,
          fetchPending: fetchPendingInAppNotifications,
          claim: claimInAppNotification,
          show: showNotificationToast,
          inFlight: inFlight.current,
        });
        if (shown > 0) console.log(`[InAppNotify] ${shown} toast(s) shown (${trigger})`);
      } catch (e) {
        // Every dependency swallows its own errors already, so this is the
        // belt: `void tick(...)` below means anything escaping here becomes an
        // unhandled rejection in Sentry, and a failed toast is not worth a
        // production error report. The next tick retries — the row is only
        // consumed by a claim that landed.
        console.warn("[InAppNotify] tick failed:", e);
      } finally {
        running = false;
      }
    };

    // A cold start mounts already-visible, so neither listener below fires on
    // its own — the same hole #623 found in EntitlementFlipListener. Without
    // this, a mom who opens the app to a notification deferred moments earlier
    // waits a full minute for it.
    void tick("mount");

    const interval = window.setInterval(() => void tick("interval"), PRESENCE_HEARTBEAT_MS);

    const onVisibilityChange = () => void tick("visibilitychange");
    const onFocus = () => void tick("focus");
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", onFocus);

    // ── Median's resume hook ───────────────────────────────────────────────
    //
    // Median delivers NO DOM lifecycle events on resume — no visibilitychange,
    // no focus. It calls a global function named median_app_resumed() if the
    // page defines one (docs.median.co/docs/app-resumed-callback). It matters
    // more here than almost anywhere: a backgrounded WKWebView throttles
    // timers, so on resume the interval may be minutes behind and her presence
    // stale. Without this the app looks away to the server for a while after
    // she has plainly come back.
    //
    // Single global slot, not a listener list — chain whatever was there and
    // restore it on cleanup rather than clobbering someone else's handler.
    const globalWindow = window as unknown as Record<string, unknown>;
    const previousResumeHandler = globalWindow.median_app_resumed;
    globalWindow.median_app_resumed = (...args: unknown[]) => {
      void tick("median_app_resumed");
      if (typeof previousResumeHandler === "function") {
        try {
          (previousResumeHandler as (...a: unknown[]) => unknown)(...args);
        } catch {
          /* someone else's handler, not ours to fix */
        }
      }
    };

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", onFocus);
      globalWindow.median_app_resumed = previousResumeHandler;
    };
  }, [user?.id]);

  return null;
};

export default InAppNotificationListener;
